import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Download, Trash2, Plus, RefreshCcw } from "lucide-react";

const TABS = ["stats", "availability", "bookings", "interest", "users", "certs", "offerings", "services", "fundraiser", "gifts", "merch", "promo", "testimonials", "emails", "settings", "contact"];

function StatCard({ label, value }) {
  return (
    <div className="card-warm !p-6" data-testid={`stat-${label}`}>
      <p className="eyebrow">{label}</p>
      <p className="mt-3 font-serif text-4xl text-forest-900">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [interest, setInterest] = useState([]);
  const [users, setUsers] = useState([]);
  const [certs, setCerts] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [fundraiser, setFundraiser] = useState({ totals: {}, entries: [] });
  const [messages, setMessages] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [services, setServices] = useState([]);
  const [merch, setMerch] = useState([]);
  const [emails, setEmails] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [promo, setPromo] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState({ voice_intro_url: "", voice_intro_caption: "", signal_number: "+31633136379", iban: "", iban_holder: "Isa Westervoorde", bank_name: "Triodos Bank", payment_note: "" });

  const reload = async () => {
    try {
      const [s, b, i, u, c, o, f, m, g, sv, mw, eq, ss, av, pm, ts] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/bookings"),
        api.get("/interest"),
        api.get("/admin/users"),
        api.get("/certificates", { params: { all_certs: true } }),
        api.get("/future-offerings", { params: { all_items: true } }),
        api.get("/fundraiser/all"),
        api.get("/contact"),
        api.get("/gift-offers"),
        api.get("/services", { params: { all_items: true } }),
        api.get("/merch-waitlist"),
        api.get("/admin/email-queue"),
        api.get("/site-settings"),
        api.get("/admin/availability"),
        api.get("/promo-materials", { params: { all_items: true } }),
        api.get("/admin/testimonials"),
      ]);
      setStats(s.data); setBookings(b.data); setInterest(i.data); setUsers(u.data);
      setCerts(c.data); setOfferings(o.data); setMessages(m.data);
      const entries = f.data;
      const totals = {};
      for (const e of entries) if (e.visible !== false) { totals[e.currency] = (totals[e.currency] || 0) + e.amount; }
      setFundraiser({ totals, entries });
      setGifts(g.data); setServices(sv.data);
      setMerch(mw.data); setEmails(eq.data);
      setSettings((prev) => ({ ...prev, ...ss.data }));
      setAvailability(av.data);
      setPromo(pm.data);
      setTestimonials(ts.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };
  useEffect(() => { reload(); }, []);

  const exportCsv = async (collection) => {
    const res = await api.get(`/admin/export/${collection}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `${collection}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-dashboard" className="container-refined py-16 md:py-24">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">{t("admin.title")}</h1>
          <p className="mt-2 text-forest-700 text-sm">Signed in as {user?.email}</p>
        </div>
        <button onClick={reload} className="btn-secondary" data-testid="admin-refresh"><RefreshCcw className="h-4 w-4" strokeWidth={1.5}/> Refresh</button>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 border-b border-forest-900/10 pb-4">
        {TABS.map((tk) => (
          <button
            key={tk}
            onClick={() => setTab(tk)}
            data-testid={`admin-tab-${tk}`}
            className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest transition ${
              tab === tk ? "bg-forest-800 text-ivory" : "text-forest-700 hover:bg-surface"
            }`}
          >
            {t(`admin.${tk}`)}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "stats" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="fundraiser €" value={`€${Number(stats.fundraiser_total_eur || 0).toFixed(0)}`} />
            <StatCard label="bookings" value={stats.bookings} />
            <StatCard label="pending" value={stats.pending_bookings} />
            <StatCard label="interest" value={stats.interest_signups} />
            <StatCard label="users" value={stats.users} />
            <StatCard label="gifts" value={stats.gift_offers} />
            <StatCard label="services" value={stats.services} />
            <StatCard label="certificates" value={stats.certificates} />
            <StatCard label="offerings" value={stats.future_offerings} />
            <StatCard label="messages" value={stats.contact_messages} />
          </div>
        )}

        {tab === "bookings" && (
          <BookingsTable rows={bookings} reload={reload} exportCsv={exportCsv} />
        )}
        {tab === "interest" && (
          <SimpleTable
            rows={interest}
            columns={[["Name","name"],["Email","email"],["Phone","phone"],["Interest","interest_type"],["Points","points_earned"],["When","created_at"]]}
            exportName="interest_signups"
            exportCsv={exportCsv}
            testId="admin-interest-table"
          />
        )}
        {tab === "users" && (
          <UsersTable rows={users} reload={reload} exportCsv={exportCsv} />
        )}
        {tab === "certs" && (
          <CertsPanel rows={certs} reload={reload} />
        )}
        {tab === "offerings" && (
          <OfferingsPanel rows={offerings} reload={reload} />
        )}
        {tab === "fundraiser" && (
          <FundraiserPanel data={fundraiser} reload={reload} exportCsv={exportCsv} />
        )}
        {tab === "services" && (
          <ServicesPanel rows={services} reload={reload} />
        )}
        {tab === "gifts" && (
          <GiftsPanel rows={gifts} reload={reload} exportCsv={exportCsv} />
        )}
        {tab === "merch" && (
          <MerchPanel rows={merch} reload={reload} exportCsv={exportCsv} />
        )}
        {tab === "emails" && (
          <EmailsPanel rows={emails} reload={reload} />
        )}
        {tab === "settings" && (
          <SettingsPanel initial={settings} reload={reload} />
        )}
        {tab === "availability" && (
          <AvailabilityPanel rows={availability} reload={reload} />
        )}
        {tab === "promo" && (
          <PromoPanel rows={promo} reload={reload} />
        )}
        {tab === "testimonials" && (
          <TestimonialsPanel rows={testimonials} reload={reload} />
        )}
        {tab === "contact" && (
          <SimpleTable
            rows={messages}
            columns={[["Name","name"],["Email","email"],["Subject","subject"],["Message","message"],["When","created_at"]]}
            exportName="contact_messages"
            exportCsv={exportCsv}
            testId="admin-contact-table"
          />
        )}
      </div>
    </div>
  );
}

function BookingsTable({ rows, reload, exportCsv }) {
  const update = async (id, status) => {
    await api.patch(`/bookings/${id}`, { status });
    toast.success("Updated");
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete booking?")) return;
    await api.delete(`/bookings/${id}`);
    reload();
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => exportCsv("bookings")} className="btn-secondary" data-testid="export-bookings"><Download className="h-4 w-4" strokeWidth={1.5}/> CSV</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-forest-900/10 bg-white/70">
        <table className="min-w-full text-sm" data-testid="admin-bookings-table">
          <thead className="bg-surface text-forest-800">
            <tr>
              {["Name","Email","Phone","Date","Time","Setup","Address","Lang","Status","Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-widest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-forest-900/5">
            {rows.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                <td className="px-4 py-3">{b.customer_name}</td>
                <td className="px-4 py-3">{b.email}</td>
                <td className="px-4 py-3">{b.phone}</td>
                <td className="px-4 py-3">{b.date}</td>
                <td className="px-4 py-3">{b.time}</td>
                <td className="px-4 py-3">{b.setup_type}</td>
                <td className="px-4 py-3 max-w-[220px] truncate">{b.address}</td>
                <td className="px-4 py-3 uppercase">{b.preferred_language}</td>
                <td className="px-4 py-3">
                  <select value={b.status} onChange={(e) => update(b.id, e.target.value)} className="rounded-lg border border-forest-900/15 bg-white px-2 py-1 text-xs" data-testid={`booking-status-${b.id}`}>
                    {["pending","confirmed","completed","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => del(b.id)} className="text-destructive text-xs" data-testid={`booking-delete-${b.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5}/></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="px-4 py-8 text-center text-forest-700" colSpan="10">No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SimpleTable({ rows, columns, exportName, exportCsv, testId }) {
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => exportCsv(exportName)} className="btn-secondary" data-testid={`export-${exportName}`}><Download className="h-4 w-4" strokeWidth={1.5}/> CSV</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-forest-900/10 bg-white/70">
        <table className="min-w-full text-sm" data-testid={testId}>
          <thead className="bg-surface text-forest-800">
            <tr>{columns.map(([h]) => <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-widest">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-forest-900/5">
            {rows.map((r) => (
              <tr key={r.id}>{columns.map(([h, k]) => <td key={h} className="px-4 py-3 max-w-[260px] truncate">{String(r[k] ?? "")}</td>)}</tr>
            ))}
            {rows.length === 0 && <tr><td className="px-4 py-8 text-center text-forest-700" colSpan={columns.length}>No entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersTable({ rows, reload, exportCsv }) {
  const adjust = async (id) => {
    const delta = parseInt(prompt("Points delta (positive or negative):"), 10);
    if (!delta || Number.isNaN(delta)) return;
    const reason = prompt("Reason (optional):") || "";
    await api.post(`/admin/users/${id}/points`, { delta, reason });
    toast.success("Points updated");
    reload();
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => exportCsv("users")} className="btn-secondary" data-testid="export-users"><Download className="h-4 w-4" strokeWidth={1.5}/> CSV</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-forest-900/10 bg-white/70">
        <table className="min-w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-surface text-forest-800">
            <tr>{["Name","Email","Lang","Role","Points","Adjust"].map((h) => <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-widest">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-forest-900/5">
            {rows.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3 uppercase">{u.preferred_language}</td>
                <td className="px-4 py-3">{u.role}</td>
                <td className="px-4 py-3 font-semibold">{u.points_total ?? 0}</td>
                <td className="px-4 py-3"><button onClick={() => adjust(u.id)} className="btn-ghost-link" data-testid={`user-points-adjust-${u.id}`}>Adjust</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CertsPanel({ rows, reload }) {
  const [form, setForm] = useState({ title: "", issuer: "", issue_date: "", notes: "", file_data: "", file_type: "image", visible: true });
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, file_data: reader.result, file_type: file.type.includes("pdf") ? "pdf" : "image" }));
    reader.readAsDataURL(file);
  };
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/certificates", form);
    toast.success("Certificate added");
    setForm({ title: "", issuer: "", issue_date: "", notes: "", file_data: "", file_type: "image", visible: true });
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/certificates/${id}`);
    reload();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="card-warm space-y-4 lg:col-span-1" data-testid="cert-form">
        <h3 className="font-serif text-2xl text-forest-900">Add certificate</h3>
        <div><label className="field-label">Title</label><input required className="field-input" data-testid="cert-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}/></div>
        <div><label className="field-label">Issuer</label><input required className="field-input" data-testid="cert-issuer" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })}/></div>
        <div><label className="field-label">Date</label><input required type="date" className="field-input" data-testid="cert-date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })}/></div>
        <div><label className="field-label">Notes</label><textarea className="field-input" data-testid="cert-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></div>
        <div><label className="field-label">File (image/PDF)</label><input type="file" accept="image/*,application/pdf" onChange={onFile} className="text-sm" data-testid="cert-file"/></div>
        <button className="btn-primary" data-testid="cert-submit"><Plus className="h-4 w-4" strokeWidth={1.5}/> Add</button>
      </form>
      <div className="lg:col-span-2 grid md:grid-cols-2 gap-4">
        {rows.map((c) => (
          <div key={c.id} className="card-warm !p-5" data-testid={`admin-cert-${c.id}`}>
            {c.file_data && c.file_type === "image" && <img src={c.file_data} alt={c.title} className="rounded-xl h-40 w-full object-cover mb-3"/>}
            <p className="eyebrow">{c.issuer}</p>
            <h4 className="font-serif text-xl text-forest-900">{c.title}</h4>
            <p className="text-xs text-forest-700">{c.issue_date}</p>
            <button onClick={() => del(c.id)} className="mt-3 text-xs text-destructive inline-flex items-center gap-1" data-testid={`admin-cert-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5}/> Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OfferingsPanel({ rows, reload }) {
  const [form, setForm] = useState({ title: "", description: "", category: "wellness", status: "coming_later", visible: true });
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/future-offerings", form);
    toast.success("Offering added");
    setForm({ title: "", description: "", category: "wellness", status: "coming_later", visible: true });
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/future-offerings/${id}`);
    reload();
  };
  const toggle = async (o) => {
    await api.patch(`/future-offerings/${o.id}`, { ...o, visible: !o.visible });
    reload();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="card-warm space-y-4 lg:col-span-1" data-testid="offer-form">
        <h3 className="font-serif text-2xl text-forest-900">Add offering</h3>
        <div><label className="field-label">Title</label><input required data-testid="offer-title" className="field-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}/></div>
        <div><label className="field-label">Description</label><textarea required data-testid="offer-desc" className="field-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/></div>
        <div><label className="field-label">Category</label>
          <select className="field-input" data-testid="offer-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {["teaching","beauty","wellness","products"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button className="btn-primary" data-testid="offer-submit"><Plus className="h-4 w-4" strokeWidth={1.5}/> Add</button>
      </form>
      <div className="lg:col-span-2 space-y-4">
        {rows.map((o) => (
          <div key={o.id} className="card-warm !p-5 flex items-start justify-between gap-4" data-testid={`admin-offer-${o.id}`}>
            <div>
              <p className="eyebrow">{o.category}</p>
              <h4 className="font-serif text-xl text-forest-900">{o.title}</h4>
              <p className="text-sm text-forest-700 mt-2">{o.description}</p>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <button onClick={() => toggle(o)} className="text-xs uppercase tracking-widest text-forest-700 hover:text-forest-900" data-testid={`admin-offer-toggle-${o.id}`}>{o.visible ? "Hide" : "Show"}</button>
              <button onClick={() => del(o.id)} className="text-xs text-destructive inline-flex items-center gap-1" data-testid={`admin-offer-delete-${o.id}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5}/> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FundraiserPanel({ data, reload, exportCsv }) {
  const [form, setForm] = useState({ amount: "", currency: "EUR", source: "", description: "", reference: "", transferred_at: "", proof_image: "", visible: true });
  const [parsing, setParsing] = useState(false);
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, proof_image: reader.result }));
    reader.readAsDataURL(file);
  };
  const parseFromScreenshot = async () => {
    if (!form.proof_image) { toast.error("Upload a screenshot first"); return; }
    setParsing(true);
    try {
      const { data: parsed } = await api.post("/admin/parse-transfer", { image_data: form.proof_image });
      setForm((f) => ({
        ...f,
        amount: parsed.amount != null ? String(parsed.amount) : f.amount,
        currency: parsed.currency || f.currency,
        transferred_at: parsed.transferred_at || f.transferred_at,
        reference: parsed.reference || f.reference,
        source: parsed.source || f.source,
      }));
      toast.success("Fields pre-filled from screenshot — review before saving");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse screenshot");
    } finally { setParsing(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/fundraiser", { ...form, amount: parseFloat(form.amount), transferred_at: form.transferred_at || null });
    toast.success("Entry added");
    setForm({ amount: "", currency: "EUR", source: "", description: "", reference: "", transferred_at: "", proof_image: "", visible: true });
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/fundraiser/${id}`);
    reload();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-4">
        <div className="card-warm">
          <p className="eyebrow">Totals sent</p>
          {Object.entries(data.totals || {}).map(([cur, amt]) => (
            <p key={cur} className="mt-3 font-serif text-3xl text-forest-900">{amt.toFixed(2)} <span className="text-sm text-forest-700 uppercase">{cur}</span></p>
          ))}
          {Object.keys(data.totals || {}).length === 0 && <p className="mt-3 text-sm text-forest-700">No entries yet.</p>}
        </div>
        <form onSubmit={submit} className="card-warm space-y-3" data-testid="fundraiser-form">
          <h3 className="font-serif text-xl text-forest-900">Log a transfer to Ukraine</h3>
          <div className="grid grid-cols-2 gap-2">
            <input required type="number" step="0.01" placeholder="Amount" className="field-input" data-testid="fund-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}/>
            <select className="field-input" data-testid="fund-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              {["EUR","USD","UAH","GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input placeholder="Source (Dzyga's Paw, Come Back Alive…)" className="field-input" data-testid="fund-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}/>
          <input placeholder="Description" className="field-input" data-testid="fund-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>
          <input placeholder="Reference (bank ref)" className="field-input" data-testid="fund-ref" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}/>
          <input type="date" className="field-input" data-testid="fund-date" value={form.transferred_at} onChange={(e) => setForm({ ...form, transferred_at: e.target.value })}/>
          <div>
            <label className="field-label">Proof screenshot</label>
            <input type="file" accept="image/*" onChange={onFile} className="text-sm" data-testid="fund-proof-file"/>
            {form.proof_image && <img alt="preview" src={form.proof_image} className="mt-2 rounded-xl border border-forest-900/10 max-h-40" />}
            {form.proof_image && (
              <button type="button" onClick={parseFromScreenshot} disabled={parsing} className="btn-secondary mt-3 !py-2 !text-[11px]" data-testid="fund-parse-btn">
                {parsing ? "Reading screenshot…" : "Auto-fill from screenshot"}
              </button>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-forest-700"><input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })}/> Publicly visible</label>
          <button className="btn-primary" data-testid="fund-submit">Add transfer</button>
        </form>
      </div>
      <div className="lg:col-span-2 space-y-4">
        <div className="flex justify-end">
          <button onClick={() => exportCsv("fundraiser")} className="btn-secondary" data-testid="export-fundraiser"><Download className="h-4 w-4" strokeWidth={1.5}/> CSV</button>
        </div>
        <div className="grid md:grid-cols-2 gap-4" data-testid="admin-fundraiser-list">
          {(data.entries || []).map((e) => (
            <div key={e.id} className="card-warm !p-4" data-testid={`admin-fund-${e.id}`}>
              {e.proof_image && <img src={e.proof_image} alt="proof" className="rounded-xl w-full h-40 object-cover mb-3"/>}
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-serif text-2xl text-forest-900">{Number(e.amount).toFixed(2)} <span className="text-sm text-forest-700 uppercase">{e.currency}</span></p>
                  <p className="text-xs text-forest-700 mt-1">{e.source} · {(e.transferred_at || e.received_at || "").slice(0,10)}</p>
                  {e.description && <p className="text-sm text-forest-700 mt-2">{e.description}</p>}
                  {e.reference && <p className="text-xs text-forest-700/80 mt-1">ref: {e.reference}</p>}
                  {e.visible === false && <span className="mt-2 inline-block text-[10px] uppercase tracking-widest text-destructive">Hidden</span>}
                </div>
                <button onClick={() => del(e.id)} className="text-destructive" data-testid={`admin-fund-delete-${e.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5}/></button>
              </div>
            </div>
          ))}
          {(!data.entries || data.entries.length === 0) && <p className="text-sm text-forest-700 md:col-span-2">No entries yet.</p>}
        </div>
      </div>
    </div>
  );
}

function ServicesPanel({ rows, reload }) {
  const [form, setForm] = useState({ name: "", description: "", duration_minutes: 60, price: 65, currency: "EUR", setup_type: "chair_at_home", active: true, order: 0 });
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/services", { ...form, duration_minutes: parseInt(form.duration_minutes, 10), price: parseFloat(form.price), order: parseInt(form.order, 10) });
    toast.success("Service added");
    setForm({ name: "", description: "", duration_minutes: 60, price: 65, currency: "EUR", setup_type: "chair_at_home", active: true, order: 0 });
    reload();
  };
  const save = async (s) => {
    await api.patch(`/services/${s.id}`, s);
    toast.success("Saved");
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete service?")) return;
    await api.delete(`/services/${id}`);
    reload();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="card-warm space-y-3 lg:col-span-1" data-testid="service-form">
        <h3 className="font-serif text-xl text-forest-900">Add a service</h3>
        <input required placeholder="Name" className="field-input" data-testid="svc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/>
        <textarea required placeholder="Description" className="field-input" data-testid="svc-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>
        <div className="grid grid-cols-2 gap-2">
          <input required type="number" placeholder="Minutes" className="field-input" data-testid="svc-mins" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}/>
          <input required type="number" step="0.01" placeholder="Price" className="field-input" data-testid="svc-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select className="field-input" data-testid="svc-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            {["EUR","USD","UAH","GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="field-input" data-testid="svc-setup" value={form.setup_type} onChange={(e) => setForm({ ...form, setup_type: e.target.value })}>
            {["chair_at_home","high_bed","massage_table"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button className="btn-primary" data-testid="svc-submit"><Plus className="h-4 w-4" strokeWidth={1.5}/> Add</button>
      </form>
      <div className="lg:col-span-2 space-y-4" data-testid="admin-services-list">
        {rows.map((s) => (
          <div key={s.id} className="card-warm !p-5" data-testid={`admin-svc-${s.id}`}>
            <div className="grid md:grid-cols-4 gap-3 items-start">
              <input className="field-input md:col-span-2" data-testid={`svc-edit-name-${s.id}`} defaultValue={s.name} onBlur={(e) => save({ ...s, name: e.target.value })} />
              <input type="number" step="0.01" className="field-input" data-testid={`svc-edit-price-${s.id}`} defaultValue={s.price} onBlur={(e) => save({ ...s, price: parseFloat(e.target.value) })} />
              <input type="number" className="field-input" data-testid={`svc-edit-mins-${s.id}`} defaultValue={s.duration_minutes} onBlur={(e) => save({ ...s, duration_minutes: parseInt(e.target.value,10) })} />
            </div>
            <textarea className="field-input mt-3" data-testid={`svc-edit-desc-${s.id}`} defaultValue={s.description} onBlur={(e) => save({ ...s, description: e.target.value })} />
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-forest-700">{s.setup_type} · {s.currency}</span>
              <div className="flex gap-3">
                <button onClick={() => save({ ...s, active: !s.active })} className="uppercase tracking-widest text-forest-700 hover:text-forest-900" data-testid={`admin-svc-toggle-${s.id}`}>{s.active ? "Hide" : "Show"}</button>
                <button onClick={() => del(s.id)} className="text-destructive inline-flex items-center gap-1" data-testid={`admin-svc-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5}/> Delete</button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-forest-700">No services yet.</p>}
      </div>
    </div>
  );
}

function GiftsPanel({ rows, reload, exportCsv }) {
  const setStatus = async (id, status) => {
    await api.patch(`/gift-offers/${id}?status=${encodeURIComponent(status)}`);
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/gift-offers/${id}`);
    reload();
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => exportCsv("gift_offers")} className="btn-secondary" data-testid="export-gift_offers"><Download className="h-4 w-4" strokeWidth={1.5}/> CSV</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-forest-900/10 bg-white/70">
        <table className="min-w-full text-sm" data-testid="admin-gifts-table">
          <thead className="bg-surface text-forest-800">
            <tr>{["Name","Email","Phone","Gift","Quantity","When","Status","Actions"].map((h) => <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-widest">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-forest-900/5">
            {rows.map((g) => (
              <tr key={g.id} data-testid={`gift-row-${g.id}`}>
                <td className="px-4 py-3">{g.name}</td>
                <td className="px-4 py-3">{g.email}</td>
                <td className="px-4 py-3">{g.phone}</td>
                <td className="px-4 py-3 capitalize">{g.gift_type}</td>
                <td className="px-4 py-3">{g.quantity}</td>
                <td className="px-4 py-3">{g.when}</td>
                <td className="px-4 py-3">
                  <select value={g.status || "offered"} onChange={(e) => setStatus(g.id, e.target.value)} className="rounded-lg border border-forest-900/15 bg-white px-2 py-1 text-xs" data-testid={`gift-status-${g.id}`}>
                    {["offered","accepted","received","declined"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3"><button onClick={() => del(g.id)} className="text-destructive" data-testid={`gift-delete-${g.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5}/></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="px-4 py-8 text-center text-forest-700" colSpan="8">No gift offers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function MerchPanel({ rows, reload, exportCsv }) {
  const del = async (id) => {
    if (!window.confirm("Remove from waitlist?")) return;
    await api.delete(`/merch-waitlist/${id}`);
    reload();
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => exportCsv("merch_waitlist")} className="btn-secondary" data-testid="export-merch_waitlist"><Download className="h-4 w-4" strokeWidth={1.5}/> CSV</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-forest-900/10 bg-white/70">
        <table className="min-w-full text-sm" data-testid="admin-merch-table">
          <thead className="bg-surface text-forest-800">
            <tr>{["Name","Email","Lang","Interests","Notes","When","Actions"].map((h) => <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-widest">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-forest-900/5">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`merch-row-${r.id}`}>
                <td className="px-4 py-3">{r.name}</td>
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3 uppercase">{r.preferred_language}</td>
                <td className="px-4 py-3 max-w-[220px] truncate">{r.interests}</td>
                <td className="px-4 py-3 max-w-[220px] truncate">{r.notes}</td>
                <td className="px-4 py-3">{(r.created_at || "").slice(0,10)}</td>
                <td className="px-4 py-3"><button onClick={() => del(r.id)} className="text-destructive" data-testid={`merch-delete-${r.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5}/></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="px-4 py-8 text-center text-forest-700" colSpan="7">No waitlist signups yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmailsPanel({ rows, reload }) {
  const [smtp, setSmtp] = useState({ configured: false });
  useEffect(() => { api.get("/admin/smtp-status").then((r) => setSmtp(r.data)).catch(() => {}); }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/admin/email-queue/${id}?status=${encodeURIComponent(status)}`);
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete email?")) return;
    await api.delete(`/admin/email-queue/${id}`);
    reload();
  };
  const sendOne = async (id) => {
    try { await api.post(`/admin/email-queue/${id}/send`); toast.success("Sent"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
  };
  const sendAll = async () => {
    try {
      const { data } = await api.post("/admin/email-queue/send-all");
      toast.success(`Sent ${data.sent}${data.failed ? ` · failed ${data.failed}` : ""}`);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
  };
  return (
    <div className="space-y-4" data-testid="admin-emails-list">
      <div className={`card-warm !p-4 flex flex-wrap items-center justify-between gap-3 ${smtp.configured ? "border-l-4 border-l-wheat-500" : "border-l-4 border-l-destructive/60"}`} data-testid="smtp-status-banner">
        <div>
          <p className="eyebrow">SMTP</p>
          <p className="text-sm text-forest-800 mt-1">
            {smtp.configured
              ? <>Sending as <strong>{smtp.user}</strong> via {smtp.host}:{smtp.port}. New booking emails go out automatically.</>
              : <>Auto-send is <strong>off</strong>. Add <code className="text-xs bg-ivory px-1 rounded">SMTP_USER</code> and <code className="text-xs bg-ivory px-1 rounded">SMTP_PASSWORD</code> (Gmail App Password) to <code className="text-xs bg-ivory px-1 rounded">/app/backend/.env</code> and restart backend.</>}
          </p>
        </div>
        {smtp.configured && rows.some((e) => e.status === "queued") && (
          <button onClick={sendAll} className="btn-primary !py-2 !text-[11px]" data-testid="smtp-send-all">Send all queued</button>
        )}
      </div>
      {rows.map((e) => (
        <div key={e.id} className="card-warm !p-5" data-testid={`email-row-${e.id}`}>
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <p className="eyebrow">{e.kind} · <span className="normal-case tracking-normal">{(e.created_at||"").slice(0,16).replace("T"," ")}</span></p>
              <h4 className="font-serif text-xl text-forest-900 mt-1">{e.subject}</h4>
              <p className="text-sm text-forest-700 mt-1">To: <strong>{e.to_name}</strong> &lt;{e.to_email}&gt;</p>
              {e.last_error && <p className="text-xs text-destructive mt-1">Last error: {e.last_error}</p>}
            </div>
            <div className="flex items-center gap-2">
              {smtp.configured && e.status !== "sent" && (
                <button onClick={() => sendOne(e.id)} className="btn-secondary !py-1.5 !text-[10px]" data-testid={`email-send-${e.id}`}>Send now</button>
              )}
              <select value={e.status || "queued"} onChange={(ev) => setStatus(e.id, ev.target.value)} className="rounded-lg border border-forest-900/15 bg-white px-2 py-1 text-xs" data-testid={`email-status-${e.id}`}>
                {["queued","sent","failed"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => del(e.id)} className="text-destructive" data-testid={`email-delete-${e.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5}/></button>
            </div>
          </div>
          <pre className="mt-4 whitespace-pre-wrap text-sm text-forest-800 leading-relaxed bg-ivory/60 rounded-xl p-4 border border-forest-900/10">{e.body}</pre>
        </div>
      ))}
      {rows.length === 0 && <p className="text-sm text-forest-700">No queued emails.</p>}
    </div>
  );
}

function SettingsPanel({ initial, reload }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [icsUrl, setIcsUrl] = useState("");
  useEffect(() => {
    api.get("/admin/calendar-url").then((r) => setIcsUrl(r.data.ics_url || "")).catch(() => {});
  }, []);
  const copyIcs = async () => {
    try { await navigator.clipboard.writeText(icsUrl); toast.success("Copied to clipboard"); }
    catch { toast.error("Could not copy"); }
  };
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Audio must be under 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, voice_intro_url: reader.result }));
    reader.readAsDataURL(file);
  };
  const save = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      await api.put("/site-settings", form);
      toast.success("Voice intro saved");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setUploading(false); }
  };
  const clear = async () => {
    if (!window.confirm("Remove voice intro?")) return;
    await api.put("/site-settings", { voice_intro_url: null, voice_intro_caption: "" });
    setForm({ voice_intro_url: "", voice_intro_caption: "" });
    reload();
  };
  return (
    <form onSubmit={save} className="card-warm max-w-2xl space-y-5" data-testid="settings-form">
      <div>
        <h3 className="font-serif text-2xl text-forest-900">Voice intro & contact settings</h3>
        <p className="mt-2 text-sm text-forest-700">Everything guests see: your welcome audio, Signal number, IBAN for iDEAL/bank transfer.</p>
      </div>
      <div>
        <label className="field-label">Audio file (mp3, wav, m4a) — up to 5 MB</label>
        <input type="file" accept="audio/*" onChange={onFile} data-testid="settings-audio-file" className="text-sm"/>
        {form.voice_intro_url && (
          <audio src={form.voice_intro_url} controls className="mt-3 w-full" data-testid="settings-audio-preview" />
        )}
      </div>
      <div>
        <label className="field-label">Caption (shown next to the player)</label>
        <input className="field-input" data-testid="settings-caption" value={form.voice_intro_caption || ""} onChange={(e) => setForm({ ...form, voice_intro_caption: e.target.value })} placeholder="A short welcome to the practice"/>
      </div>
      <div className="pt-4 border-t border-forest-900/10">
        <p className="eyebrow">Contact & payment</p>
      </div>
      <div>
        <label className="field-label">Signal number (international format, e.g. +31633136379)</label>
        <input className="field-input" data-testid="settings-signal" value={form.signal_number || ""} onChange={(e) => setForm({ ...form, signal_number: e.target.value })} placeholder="+31633136379"/>
      </div>
      <div>
        <label className="field-label">WhatsApp number (international format, e.g. +31633136379)</label>
        <input className="field-input" data-testid="settings-whatsapp" value={form.whatsapp_number || ""} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="+31633136379"/>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="field-label">IBAN (Triodos)</label>
          <input className="field-input" data-testid="settings-iban" value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="NL00 TRIO 0000 0000 00"/>
        </div>
        <div>
          <label className="field-label">Account holder</label>
          <input className="field-input" data-testid="settings-iban-holder" value={form.iban_holder || ""} onChange={(e) => setForm({ ...form, iban_holder: e.target.value })} placeholder="Isa Westervoorde"/>
        </div>
        <div>
          <label className="field-label">Bank name</label>
          <input className="field-input" data-testid="settings-bank" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Triodos Bank"/>
        </div>
        <div>
          <label className="field-label">Extra payment note (optional)</label>
          <input className="field-input" data-testid="settings-payment-note" value={form.payment_note || ""} onChange={(e) => setForm({ ...form, payment_note: e.target.value })} placeholder="Use booking id as reference"/>
        </div>
      </div>
      <div className="flex gap-3">
        <button className="btn-primary" disabled={uploading} data-testid="settings-save">{uploading ? "Saving…" : "Save settings"}</button>
        {form.voice_intro_url && (
          <button type="button" onClick={clear} className="btn-secondary" data-testid="settings-clear">Remove voice intro</button>
        )}
      </div>
      <div className="pt-6 mt-2 border-t border-forest-900/10 space-y-3" data-testid="settings-calendar-feed">
        <p className="eyebrow">Strato Agenda sync</p>
        <p className="text-sm text-forest-700">
          Add this URL to your Strato Webmail as an external calendar subscription. Every new booking will appear automatically. Do <strong>not</strong> share this URL — it contains your private access token.
        </p>
        <div className="flex items-stretch gap-2">
          <input readOnly value={icsUrl || "loading…"} className="field-input flex-1 !text-xs" data-testid="settings-ics-url" onFocus={(e) => e.target.select()} />
          <button type="button" onClick={copyIcs} className="btn-secondary !px-4" data-testid="settings-ics-copy">Copy</button>
        </div>
        <p className="text-xs text-forest-700/80">
          In Strato Webmail: <em>Agenda → Externe agenda toevoegen → URL/iCal</em>, plak deze link. Refresh-interval kan je meestal op 15 min zetten.
        </p>
      </div>
    </form>
  );
}

function AvailabilityPanel({ rows, reload }) {
  const [form, setForm] = useState({ date: "", time: "", note: "" });
  const [bulk, setBulk] = useState({ date: "", start: "10:00", end: "16:00", step: 60 });
  const add = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time) return toast.error("Pick date and time");
    try {
      await api.post("/admin/availability", form);
      toast.success("Slot added");
      setForm({ date: "", time: "", note: "" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const addBulk = async (e) => {
    e.preventDefault();
    if (!bulk.date) return toast.error("Pick a date");
    const step = parseInt(bulk.step, 10) || 60;
    const [sh, sm] = bulk.start.split(":").map(Number);
    const [eh, em] = bulk.end.split(":").map(Number);
    const times = [];
    for (let m = sh * 60 + sm; m <= eh * 60 + em; m += step) {
      const h = String(Math.floor(m / 60)).padStart(2, "0");
      const mm = String(m % 60).padStart(2, "0");
      times.push(`${h}:${mm}`);
    }
    let added = 0, skipped = 0;
    for (const t of times) {
      try { await api.post("/admin/availability", { date: bulk.date, time: t }); added++; }
      catch { skipped++; }
    }
    toast.success(`Added ${added} slot(s)${skipped ? ` · skipped ${skipped}` : ""}`);
    reload();
  };
  const toggle = async (slot) => {
    const target = slot.status === "blocked" ? "open" : "blocked";
    try {
      await api.patch(`/admin/availability/${slot.id}?status=${target}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this slot?")) return;
    try { await api.delete(`/admin/availability/${id}`); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const generateDefault = async () => {
    if (!window.confirm("Generate default slots for the next 6 weeks (Tue/Wed/Thu at 18:00, 19:00 and 20:00)?\nExisting slots stay untouched.")) return;
    try {
      const r = await api.post("/admin/availability/generate", null, { params: { weeks: 6 } });
      toast.success(`Added ${r.data.created} new slot(s)`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  // group by date
  const byDate = rows.reduce((acc, r) => { (acc[r.date] = acc[r.date] || []).push(r); return acc; }, {});
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-4">
        <form onSubmit={add} className="card-warm space-y-3" data-testid="avail-single-form">
          <h3 className="font-serif text-xl text-forest-900">Add a slot</h3>
          <div className="grid grid-cols-2 gap-2">
            <input required type="date" className="field-input" data-testid="avail-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}/>
            <input required type="time" step="900" className="field-input" data-testid="avail-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}/>
          </div>
          <input placeholder="Note (optional)" className="field-input" data-testid="avail-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}/>
          <button className="btn-primary" data-testid="avail-add"><Plus className="h-4 w-4" strokeWidth={1.5}/> Add slot</button>
        </form>
        <div className="card-warm space-y-3" data-testid="avail-auto-panel">
          <h3 className="font-serif text-xl text-forest-900">Auto-generate default availability</h3>
          <p className="text-xs text-forest-700">Tue / Wed / Thu · 18:00 · 19:00 · 20:00 · next 6 weeks. Existing slots stay untouched.</p>
          <button type="button" onClick={generateDefault} className="btn-secondary" data-testid="avail-auto-generate"><Plus className="h-4 w-4" strokeWidth={1.5}/> Generate 6 weeks</button>
        </div>
        <form onSubmit={addBulk} className="card-warm space-y-3" data-testid="avail-bulk-form">
          <h3 className="font-serif text-xl text-forest-900">Add a whole day</h3>
          <input required type="date" className="field-input" data-testid="avail-bulk-date" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })}/>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="field-label">Start</label>
              <input required type="time" step="900" className="field-input" data-testid="avail-bulk-start" value={bulk.start} onChange={(e) => setBulk({ ...bulk, start: e.target.value })}/>
            </div>
            <div>
              <label className="field-label">End</label>
              <input required type="time" step="900" className="field-input" data-testid="avail-bulk-end" value={bulk.end} onChange={(e) => setBulk({ ...bulk, end: e.target.value })}/>
            </div>
            <div>
              <label className="field-label">Step (min)</label>
              <input required type="number" min="15" step="15" className="field-input" data-testid="avail-bulk-step" value={bulk.step} onChange={(e) => setBulk({ ...bulk, step: e.target.value })}/>
            </div>
          </div>
          <button className="btn-secondary" data-testid="avail-bulk-add">Generate slots</button>
        </form>
      </div>
      <div className="lg:col-span-2 space-y-6" data-testid="avail-list">
        {Object.keys(byDate).sort().map((date) => (
          <div key={date} className="card-warm !p-5" data-testid={`avail-date-${date}`}>
            <p className="eyebrow">{new Date(date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {byDate[date].sort((a,b) => a.time.localeCompare(b.time)).map((s) => (
                <div
                  key={s.id}
                  data-testid={`avail-slot-${s.id}`}
                  className={`inline-flex items-center gap-2 rounded-full border pl-4 pr-2 py-1 text-sm ${
                    s.status === "booked" ? "border-wheat-500 bg-wheat-50 text-forest-900" :
                    s.status === "blocked" ? "border-forest-900/20 bg-forest-900/5 text-forest-700 line-through" :
                    "border-forest-900/15 bg-white text-forest-800"
                  }`}
                >
                  <span className="font-semibold">{s.time}</span>
                  <span className="text-[10px] uppercase tracking-widest opacity-70">{s.status}</span>
                  {s.status !== "booked" && (
                    <>
                      <button onClick={() => toggle(s)} className="text-forest-700 hover:text-forest-900 text-xs" data-testid={`avail-toggle-${s.id}`}>{s.status === "blocked" ? "open" : "block"}</button>
                      <button onClick={() => del(s.id)} className="text-destructive" data-testid={`avail-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5}/></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-forest-700">No slots yet — add one on the left, or generate a whole day.</p>}
      </div>
    </div>
  );
}


function PromoPanel({ rows, reload }) {
  const [form, setForm] = useState({ title: "", description: "", file_data: "", file_type: "image", external_url: "", visible: true });
  const [uploading, setUploading] = useState(false);
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File must be under 8 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const isPdf = file.type === "application/pdf";
      setForm((f) => ({ ...f, file_data: reader.result, file_type: isPdf ? "pdf" : "image" }));
    };
    reader.readAsDataURL(file);
  };
  const add = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Give it a title");
    if (!form.file_data && !form.external_url) return toast.error("Upload a file or paste a link");
    setUploading(true);
    try {
      await api.post("/promo-materials", form);
      toast.success("Promo material added");
      setForm({ title: "", description: "", file_data: "", file_type: "image", external_url: "", visible: true });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setUploading(false); }
  };
  const toggle = async (it) => {
    try {
      await api.patch(`/promo-materials/${it.id}`, { ...it, visible: !it.visible });
      reload();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    try { await api.delete(`/promo-materials/${id}`); reload(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="promo-panel">
      <form onSubmit={add} className="card-warm space-y-4 lg:col-span-1" data-testid="promo-form">
        <h3 className="font-serif text-xl text-forest-900">Add promo material</h3>
        <div>
          <label className="field-label">Title</label>
          <input required className="field-input" data-testid="promo-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="A5 flyer — Reiki for Ukraine"/>
        </div>
        <div>
          <label className="field-label">Description (optional)</label>
          <textarea rows={3} className="field-input" data-testid="promo-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Print at 100% — pass to friends."/>
        </div>
        <div>
          <label className="field-label">Upload file (image or PDF, ≤ 8 MB)</label>
          <input type="file" accept="image/*,application/pdf" onChange={onFile} data-testid="promo-file" className="text-sm"/>
          {form.file_data && form.file_type === "image" && (
            <img src={form.file_data} alt="preview" className="mt-3 max-h-40 rounded-lg border border-forest-900/10"/>
          )}
          {form.file_data && form.file_type === "pdf" && (
            <p className="mt-2 text-xs text-forest-700">PDF ready ({Math.round(form.file_data.length / 1024)} kB)</p>
          )}
        </div>
        <div>
          <label className="field-label">…or paste an external link (Drive, Dropbox, etc.)</label>
          <input className="field-input" data-testid="promo-external" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value, file_type: e.target.value ? "link" : form.file_type })} placeholder="https://…"/>
        </div>
        <label className="flex items-center gap-2 text-sm text-forest-800">
          <input type="checkbox" data-testid="promo-visible" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })}/>
          Visible on /promo
        </label>
        <button className="btn-primary" disabled={uploading} data-testid="promo-submit">
          <Plus className="h-4 w-4" strokeWidth={1.5}/> {uploading ? "Saving…" : "Add"}
        </button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {rows.length === 0 && <p className="text-sm text-forest-700">No promo material yet.</p>}
        {rows.map((it) => (
          <div key={it.id} data-testid={`promo-row-${it.id}`} className="card-warm !p-4 flex gap-4 items-start">
            {it.file_data && it.file_type === "image" ? (
              <img src={it.file_data} alt="" className="h-16 w-16 rounded-lg object-cover border border-forest-900/10"/>
            ) : (
              <div className="h-16 w-16 rounded-lg bg-forest-900/5 flex items-center justify-center text-forest-700">
                {it.file_type === "pdf" ? "PDF" : "LINK"}
              </div>
            )}
            <div className="flex-1">
              <p className="font-semibold text-forest-900">{it.title} {!it.visible && <span className="ml-2 text-[10px] uppercase tracking-widest text-wheat-700">hidden</span>}</p>
              {it.description && <p className="mt-1 text-xs text-forest-700 leading-relaxed">{it.description}</p>}
              {it.external_url && <p className="mt-1 text-[10px] text-forest-700/70 truncate">{it.external_url}</p>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggle(it)} className="btn-ghost-link !text-[11px]" data-testid={`promo-toggle-${it.id}`}>{it.visible ? "Hide" : "Show"}</button>
              <button onClick={() => del(it.id)} className="text-destructive" data-testid={`promo-delete-${it.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function TestimonialsPanel({ rows, reload }) {
  const moderate = async (id, fields) => {
    try {
      await api.patch(`/admin/testimonials/${id}`, fields);
      toast.success("Updated");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); reload(); }
    catch { toast.error("Failed"); }
  };
  const pending = rows.filter((r) => !r.approved);
  const published = rows.filter((r) => r.approved);
  return (
    <div className="space-y-8" data-testid="testimonials-panel">
      <div>
        <h3 className="font-serif text-2xl text-forest-900">Awaiting your review ({pending.length})</h3>
        {pending.length === 0 && <p className="mt-2 text-sm text-forest-700">Nothing new right now.</p>}
        <div className="mt-4 space-y-3">
          {pending.map((tt) => (
            <div key={tt.id} data-testid={`testimonial-row-${tt.id}`} className="card-warm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-wheat-700">{tt.name} · {tt.preferred_language?.toUpperCase()} {typeof tt.rating === "number" ? ` · ${tt.rating}/5` : ""}</p>
                  <p className="mt-2 font-serif text-lg text-forest-900 leading-relaxed">“{tt.quote}”</p>
                  <p className="mt-2 text-[10px] text-forest-700/70">Received {tt.created_at}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => moderate(tt.id, { approved: true })} className="btn-primary !py-1.5 !text-[11px]" data-testid={`testimonial-approve-${tt.id}`}>Approve</button>
                  <button onClick={() => del(tt.id)} className="text-destructive text-xs" data-testid={`testimonial-delete-${tt.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-serif text-2xl text-forest-900">Published ({published.length})</h3>
        <div className="mt-4 space-y-3">
          {published.map((tt) => (
            <div key={tt.id} data-testid={`testimonial-published-${tt.id}`} className="card-warm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-wheat-700">{tt.name} {typeof tt.rating === "number" ? ` · ${tt.rating}/5` : ""} {!tt.visible ? " · hidden" : ""}</p>
                  <p className="mt-2 font-serif text-lg text-forest-900 leading-relaxed">“{tt.quote}”</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => moderate(tt.id, { visible: !tt.visible })} className="btn-ghost-link !text-[11px]" data-testid={`testimonial-toggle-${tt.id}`}>{tt.visible ? "Hide" : "Show"}</button>
                  <button onClick={() => moderate(tt.id, { approved: false })} className="btn-ghost-link !text-[11px]" data-testid={`testimonial-unapprove-${tt.id}`}>Un-approve</button>
                  <button onClick={() => del(tt.id)} className="text-destructive text-xs" data-testid={`testimonial-delete-${tt.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.5}/></button>
                </div>
              </div>
            </div>
          ))}
          {published.length === 0 && <p className="text-sm text-forest-700">Nothing published yet.</p>}
        </div>
      </div>
    </div>
  );
}

